import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ListFilter, Plus } from 'lucide-react';
import { useWatchlists } from '../../hooks/useWatchlists';
import { useTheme } from '../../contexts/ThemeContext';

interface WatchlistSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectStock: (ticker: string, companyName: string, prefix: string) => void;
  selectedResearchModePrefix: string;
  style?: React.CSSProperties;
}

export const WatchlistSelectModal: React.FC<WatchlistSelectModalProps> = ({
  isOpen,
  onClose,
  onSelectStock,
  selectedResearchModePrefix,
  style
}) => {
  const { isDarkMode } = useTheme();
  const { watchlists, savedItems } = useWatchlists();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (isMobile) {
        window.scrollTo(0, 0);
      }
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, isMobile]);

  if (!isOpen) return null;

  const MobileModal = () => (
    <motion.div
      initial={{ opacity: 0, y: 100 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 100 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-x-0 bottom-0 z-50 max-h-[90vh] overflow-y-auto"
      style={{ 
        borderTopLeftRadius: '1rem',
        borderTopRightRadius: '1rem',
      }}
    >
      <div className={`rounded-t-xl shadow-lg overflow-hidden ${
        isDarkMode 
          ? 'bg-gray-900 border-t border-x border-gray-700' 
          : 'bg-white border-t border-x border-gray-200'
      }`}>
        <div className="flex justify-center py-2">
          <div className="w-12 h-1.5 rounded-full bg-gray-500/30"></div>
        </div>
        
        {/* Header */}
        <div className={`px-4 py-3 flex items-center justify-between border-b ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h3 className={`text-lg font-medium ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>Add from Watchlists</h3>
          <button
            onClick={onClose}
            className={`rounded-lg p-2 ${
              isDarkMode 
                ? 'hover:bg-gray-800 text-gray-400' 
                : 'hover:bg-gray-100 text-gray-500'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="py-1 overflow-y-auto" style={{ maxHeight: 'calc(70vh - 120px)' }}>
          {watchlists && watchlists.length > 0 ? ( 
            <div>
              {watchlists.map(list => (
                <div key={list.id} className="px-4 py-3">
                  <div className={`text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>{list.name}</div>
                  
                  {list.items.length > 0 ? (
                    <div className="space-y-1">
                      {list.items.map(item => (
                        <button
                          key={item.id}
                          className={`w-full flex items-center justify-between p-2.5 rounded-lg ${
                            isDarkMode 
                              ? 'hover:bg-gray-800 text-gray-200' 
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => {
                            onSelectStock(
                              item.identifier,
                              item.title,
                              selectedResearchModePrefix
                            );
                            onClose();
                          }}
                        >
                          <div className="flex items-center">
                            <div className="w-7 h-7 mr-2 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden">
                              <img
                                src={`/api/market/company-logo/${item.identifier}`}
                                alt={item.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  e.currentTarget.parentElement!.innerHTML = `<span class="text-xs font-bold">${item.identifier[0]}</span>`;
                                }}
                              />
                            </div>
                            <div>
                              <div className="font-mono">{item.identifier}</div>
                              <div className="text-xs truncate max-w-[200px] text-gray-400">{item.title}</div>
                            </div>
                          </div>
                          <ChevronRight size={16} className="text-gray-500" />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className={`text-xs text-center py-3 ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-400'
                    }`}>No stocks in this list</div>
                  )}
                </div>
              ))}
            </div>
          ) : savedItems && savedItems.length > 0 ? (
            <div className="px-4 py-3">
              <div className={`text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>Saved Stocks</div>
              <div className="space-y-1">
                {savedItems.filter(item => item.type === 'stock').map(item => (
                  <button
                    key={item.id}
                    className={`w-full flex items-center justify-between p-2.5 rounded-lg ${
                      isDarkMode 
                        ? 'hover:bg-gray-800 text-gray-200' 
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                    onClick={() => {
                      onSelectStock(
                        item.identifier,
                        item.title,
                        selectedResearchModePrefix
                      );
                      onClose();
                    }}
                  >
                    <div className="flex items-center">
                      <div className="w-7 h-7 mr-2 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden">
                        <img
                          src={`/api/market/company-logo/${item.identifier}`}
                          alt={item.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                            e.currentTarget.parentElement!.innerHTML = `<span class="text-xs font-bold">${item.identifier[0]}</span>`;
                          }}
                        />
                      </div>
                      <div>
                        <div className="font-mono">{item.identifier}</div>
                        <div className="text-xs truncate max-w-[200px] text-gray-400">{item.title}</div>
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-gray-500" />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className={`px-4 py-8 text-center ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              <ListFilter className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm mb-3">No watchlists or saved stocks found</p>
              <a 
                href="/dashboard/watchlists" 
                className={`inline-block px-4 py-2 rounded-lg ${
                  isDarkMode ? 'bg-gray-800 text-blue-400' : 'bg-gray-100 text-blue-600'
                } text-sm`}
              >
                Create a watchlist
              </a>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`p-4 border-t ${
          isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
        }`}>
          <button
            className={`w-full flex items-center justify-center py-2.5 px-4 rounded-lg ${
              isDarkMode 
                ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
            }`}
            onClick={() => {
              // Navigate to stock search or integrate with existing search UI
              onClose();
            }}
          >
            <Plus size={16} className="mr-2" />
            <span>Search for a stock</span>
          </button>
        </div>
      </div>
    </motion.div>
  );

  const DesktopModal = () => (
    <motion.div
      initial={{ scale: 0.95, opacity: 0, y: -10 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      exit={{ scale: 0.95, opacity: 0, y: -20 }}
      style={{
        ...style,
        position: 'fixed',
        top: '40%', // Position it higher up on desktop
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 9999,
        maxWidth: '90vw',
        width: '450px'
      }}
      className={`overflow-hidden rounded-xl ${
        isDarkMode ? 'bg-gray-900' : 'bg-white'
      } shadow-xl transition-all`}
    >
      {/* Header */}
      <div className={`p-4 flex items-center justify-between border-b ${
        isDarkMode ? 'border-gray-700' : 'border-gray-200'
      }`}>
        <h3 className={`text-lg font-medium ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>Add from Watchlists</h3>
        <button
          onClick={onClose}
          className={`rounded-lg p-2 ${
            isDarkMode 
              ? 'hover:bg-gray-800 text-gray-400' 
              : 'hover:bg-gray-100 text-gray-500'
          }`}
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      {/* Content */}
      <div className="py-1 max-h-[60vh] overflow-y-auto">
        {watchlists && watchlists.length > 0 ? ( 
          <div>
            {watchlists.map(list => (
              <div key={list.id} className="px-4 py-3">
                <div className={`text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>{list.name}</div>
                
                {list.items.length > 0 ? (
                  <div className="space-y-1">
                    {list.items.map(item => (
                      <button
                        key={item.id}
                        className={`w-full flex items-center justify-between p-2.5 rounded-lg ${
                          isDarkMode 
                            ? 'hover:bg-gray-800 text-gray-200' 
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                        onClick={() => {
                          onSelectStock(
                            item.identifier,
                            item.title,
                            selectedResearchModePrefix
                          );
                          onClose();
                        }}
                      >
                        <div className="flex items-center">
                          <div className="w-7 h-7 mr-2 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden">
                            <img
                              src={`/api/market/company-logo/${item.identifier}`}
                              alt={item.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                                e.currentTarget.parentElement!.innerHTML = `<span class="text-xs font-bold">${item.identifier[0]}</span>`;
                              }}
                            />
                          </div>
                          <div>
                            <div className="font-mono">{item.identifier}</div>
                            <div className="text-xs truncate max-w-[200px] text-gray-400">{item.title}</div>
                          </div>
                        </div>
                        <ChevronRight size={16} className="text-gray-500" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className={`text-xs text-center py-3 ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                  }`}>No stocks in this list</div>
                )}
              </div>
            ))}
          </div>
        ) : savedItems && savedItems.length > 0 ? (
          <div className="px-4 py-3">
            <div className={`text-sm font-medium mb-2 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>Saved Stocks</div>
            <div className="space-y-1">
              {savedItems.filter(item => item.type === 'stock').map(item => (
                <button
                  key={item.id}
                  className={`w-full flex items-center justify-between p-2.5 rounded-lg ${
                    isDarkMode 
                      ? 'hover:bg-gray-800 text-gray-200' 
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                  onClick={() => {
                    onSelectStock(
                      item.identifier,
                      item.title,
                      selectedResearchModePrefix
                    );
                    onClose();
                  }}
                >
                  <div className="flex items-center">
                    <div className="w-7 h-7 mr-2 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden">
                      <img
                        src={`/api/market/company-logo/${item.identifier}`}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          e.currentTarget.parentElement!.innerHTML = `<span class="text-xs font-bold">${item.identifier[0]}</span>`;
                        }}
                      />
                    </div>
                    <div>
                      <div className="font-mono">{item.identifier}</div>
                      <div className="text-xs truncate max-w-[200px] text-gray-400">{item.title}</div>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-gray-500" />
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className={`px-4 py-8 text-center ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>
            <ListFilter className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="text-sm mb-3">No watchlists or saved stocks found</p>
            <a 
              href="/dashboard/watchlists" 
              className={`inline-block px-4 py-2 rounded-lg ${
                isDarkMode ? 'bg-gray-800 text-blue-400' : 'bg-gray-100 text-blue-600'
              } text-sm`}
            >
              Create a watchlist
            </a>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={`p-4 border-t ${
        isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
      }`}>
        <button
          className={`w-full flex items-center justify-center py-2.5 px-4 rounded-lg ${
            isDarkMode 
              ? 'bg-gray-700 hover:bg-gray-600 text-white' 
              : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
          }`}
          onClick={() => {
            // Navigate to stock search or integrate with existing search UI
            onClose();
          }}
        >
          <Plus size={16} className="mr-2" />
          <span>Search for a stock</span>
        </button>
      </div>
    </motion.div>
  );

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className={`fixed font-sans inset-0 z-[9999] flex items-center justify-center ${
          isMobile ? 'items-end' : 'p-4'
        } ${isDarkMode ? 'bg-black/50 backdrop-blur-sm' : 'bg-black/50'}`}>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0" 
            onClick={onClose}
          />

          {isMobile ? <MobileModal /> : <DesktopModal />}
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};